import { TOrder } from "@core/types";
import { IonSpinner, IonIcon } from "@ionic/react";
import { informationCircle, scanOutline } from "ionicons/icons";
import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import { Placemark, Map } from "react-yandex-maps";
import { useGetOrderTrackingQuery } from "../../../providers/Orders";
import yellowCarSvg from "../../../assets/images/yellow-car.png";

interface OrderTrackingProps {
  order: TOrder;
  onInteractStart?: () => void;
  onInteractEnd?: () => void;
}

const END_INTERACTION_DELAY_MS = 220;
const HEADQUARTERS = [43.315434, 45.68801];

const orderPlacemarkOptions = { preset: "islands#dotIcon", iconColor: "#FCA42A" };

const mapOptions = {
  suppressMapOpenBlock: true,
  suppressObsoleteBrowserNotifier: true,
  yandexMapDisablePoiInteractivity: true,
};

const carPlacemarkOptions = {
  iconLayout: "default#image",
  iconImageHref: yellowCarSvg,
  iconImageSize: [40, 20] as [number, number],
  iconImageOffset: [-20, -10] as [number, number],
};

const OrderTracking = ({ order, onInteractStart, onInteractEnd }: OrderTrackingProps) => {
  const pointerActiveRef = useRef<boolean>(false);
  const [map, setMap] = useState<ymaps.Map | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState<boolean>(false);

  const {
    data: orderTracking,
    isLoading,
    error,
  } = useGetOrderTrackingQuery({
    orderTrackingPayload: { orderId: order.id },
    options: {
      refetchInterval: 10000,
      refetchOnWindowFocus: false,
    },
  });

  const orderLocationMemo = useMemo(() => {
    const lat = Number(order.address?.latitude);
    const lon = Number(order.address?.longitude);
    return Number.isFinite(lat) && Number.isFinite(lon) ? ([lat, lon] as number[]) : null;
  }, [order.address?.latitude, order.address?.longitude]);

  const driverLocationMemo = useMemo(() => {
    const lat = Number(orderTracking?.currentLatitude);
    const lon = Number(orderTracking?.currentLongitude);
    return Number.isFinite(lat) && Number.isFinite(lon) ? ([lat, lon] as number[]) : null;
  }, [orderTracking?.currentLatitude, orderTracking?.currentLongitude]);

  const defaultState = useMemo(
    () => ({
      center: (orderLocationMemo && orderLocationMemo) || (driverLocationMemo && driverLocationMemo) || HEADQUARTERS,
      zoom: 17,
      controls: [] as any[],
    }),
    [orderLocationMemo, driverLocationMemo],
  );

  const handleFitToPlacemarks = useCallback(() => {
    if (!map) return;

    const points: number[][] = [];
    if (orderLocationMemo) points.push(orderLocationMemo);
    if (driverLocationMemo) points.push(driverLocationMemo);

    if (points.length === 0) return;

    if (points.length === 1) {
      map.setCenter(points[0], 17, { duration: 900 });
      return;
    }

    const [aLat, aLon] = points[0];
    const [bLat, bLon] = points[1];

    const south = Math.min(aLat, bLat);
    const north = Math.max(aLat, bLat);
    const west = Math.min(aLon, bLon);
    const east = Math.max(aLon, bLon);

    const bounds: number[][] = [
      [south, west],
      [north, east],
    ];

    map.setBounds(bounds as any, {
      checkZoomRange: true,
      duration: 100,
      zoomMargin: [32, 32, 32, 32],
    });
  }, [map, orderLocationMemo, driverLocationMemo]);

  const endInteraction = useCallback(() => {
    if (pointerActiveRef.current) {
      setTimeout(() => onInteractEnd?.(), END_INTERACTION_DELAY_MS);
    }
    pointerActiveRef.current = false;
  }, [onInteractEnd]);

  const setMapInstanceRef = useCallback((ref: any) => {
    if (ref) setMap(ref as unknown as ymaps.Map);
  }, []);

  const handlePointerDown = useCallback(() => {
    pointerActiveRef.current = true;
    onInteractStart?.();
  }, [onInteractStart]);

  if (!orderTracking && !isLoading && !!error) {
    return null;
  }

  return (
    <div
      className="relative w-full h-96 touch-pan-x touch-pan-y transform translate-z-0 will-change-transform will-change-opacity isolate contain-layout-paint-style"
      onPointerDown={handlePointerDown}
      onPointerUp={endInteraction}
      onPointerCancel={endInteraction}
      onPointerLeave={endInteraction}
      style={{ WebkitTapHighlightColor: "transparent" }}
    >
      <div className="absolute left-2.5 top-2.5 z-10 pointer-events-none flex items-center gap-1 rounded-lg border border-gray-200 bg-white/95 shadow-md p-2 text-gray-500 text-[0.375rem]">
        <IonIcon icon={informationCircle} className="w-3 h-3" />
        <span className="text-[0.5rem]">Отслеживайте заказ на карте</span>
      </div>
      {!isMapLoaded && (
        <div className="h-full w-full flex items-center justify-center">
          <IonSpinner name="lines" />
        </div>
      )}
      <Map
        onLoad={() => setIsMapLoaded(true)}
        defaultState={defaultState}
        options={mapOptions}
        className="!rounded-xl !h-full !w-full"
        instanceRef={setMapInstanceRef}
      >
        {orderLocationMemo && <Placemark geometry={orderLocationMemo} options={orderPlacemarkOptions} />}
        {driverLocationMemo && <Placemark geometry={driverLocationMemo} options={carPlacemarkOptions} />}
      </Map>
      {isMapLoaded && (
        <>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleFitToPlacemarks();
            }}
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onPointerUp={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="flex items-center justify-center absolute bottom-10 right-2.5 bg-primary rounded-full p-2 z-10 transform translate-z-0 will-change-transform will-change-opacity isolate contain-layout-paint-style antialiased"
          >
            <IonIcon icon={scanOutline} className="transform translate-z-0 will-change-transform will-change-opacity" />
          </button>
        </>
      )}
    </div>
  );
};

export default memo(OrderTracking);
